import React from 'react';

import { Body2, H6 } from '@rbilabs/components-library/build/components/typography';
import { closestTo, format, parse } from 'date-fns';
import { capitalize } from 'lodash';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { Picture } from 'components/picture';
import { IValidDayPart } from 'state/day-part/hooks/use-active-day-parts';
import { DAYPART_FORMAT, TWELVE_HOUR_TIME_PARSE_FORMAT } from 'utils/restaurant/constants';

interface IProps {
  dayParts: ReadonlyArray<IValidDayPart>;
  itemDayParts: string[];
  marginAndBorders: boolean;
}

type WrapperProps = Pick<IProps, 'marginAndBorders'>;

const marginAndBordersCSS = css`
  margin-block-start: 2rem;
  margin-block-end: 1rem;
  margin-inline: 0;
  padding: 1rem 0;
  border-block-start: 1px solid ${Styles.color.grey.five};
  border-block-end: 1px solid ${Styles.color.grey.five};
`;

const ContentWrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  justify-content: center;
  ${({ marginAndBorders }) => marginAndBorders && marginAndBordersCSS}
`;

const IconWrapper = styled.div`
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  & p {
    display: inline-block;
    margin: 0;
    color: ${Styles.color.black};
  }
`;

export const ItemUnavailableMessage = ({ dayParts, itemDayParts, marginAndBorders }: IProps) => {
  const { formatMessage } = useIntl();

  const now = new Date();
  const itemDayPartObjects = dayParts.filter(part => itemDayParts.includes(part.key));
  const itemDayPartBoundaryDateObjects = itemDayPartObjects.flatMap(part => [
    parse(part.startTime, DAYPART_FORMAT, now),
    parse(part.endTime, DAYPART_FORMAT, now),
  ]);
  const closestDayPartBoundaryDateObj = closestTo(now, itemDayPartBoundaryDateObjects);
  // @ts-ignore
  const closestDayPartBoundaryString = format(closestDayPartBoundaryDateObj ?? 0, DAYPART_FORMAT);
  const closestItemDayPartObject = itemDayPartObjects.find(part =>
    Object.values(part).some(time => time === closestDayPartBoundaryString)
  );

  if (!closestItemDayPartObject) {
    return null;
  }

  const { startTime, endTime, key, displayName, icon } = closestItemDayPartObject;
  const dayPartName = displayName?.locale || key;

  let dayPartMessage = '';
  let timeBoundaryMessage = '';
  const nowInDayPartFormat = format(now, DAYPART_FORMAT);

  if (nowInDayPartFormat < startTime) {
    dayPartMessage = formatMessage(
      { id: 'daypartBeforeStartTime' },
      { dayPart: dayPartName.toLowerCase() }
    );
    timeBoundaryMessage = formatMessage(
      { id: 'daypartStartsAt' },
      {
        dayPart: capitalize(dayPartName),
        startTime: format(parse(startTime, DAYPART_FORMAT, now), TWELVE_HOUR_TIME_PARSE_FORMAT),
      }
    );
  } else {
    dayPartMessage = formatMessage(
      { id: 'daypartAfterEndTime' },
      { dayPart: dayPartName.toLowerCase() }
    );
    timeBoundaryMessage = formatMessage(
      { id: 'daypartEndsAt' },
      {
        dayPart: capitalize(dayPartName),
        endTime: format(parse(endTime, DAYPART_FORMAT, now), TWELVE_HOUR_TIME_PARSE_FORMAT),
      }
    );
  }

  return (
    <ContentWrapper marginAndBorders={!!marginAndBorders}>
      <IconWrapper>
        <Picture image={icon} alt={dayPartName} />
      </IconWrapper>
      <TextWrapper>
        {dayPartMessage && <H6>{dayPartMessage}</H6>}
        {timeBoundaryMessage && <Body2>{timeBoundaryMessage}</Body2>}
      </TextWrapper>
    </ContentWrapper>
  );
};
